import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaWhatsapp,
  FaInstagram,
  FaBuilding,
  FaRocket,
  FaBookOpen,
  FaUserMd,
  FaBriefcase,
  FaCogs,
  FaSearch,
  FaTools,
  FaCheckCircle,
  FaClipboardList,
  FaFileAlt,
  FaStar,
  FaEnvelope,
} from 'react-icons/fa';
import { useSmoothScroll } from '../hooks/useSmoothScroll';
import { useScrollAnimations } from '../hooks/useScrollAnimations';
import PageShell from '../components/PageShell';
import Projetos from '../components/Projetos';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { siteConfig } from '../data/site';
import { projects } from '../data/projects';
import monisePhoto from '../assets/monise.png';

const HERO_BG = '/assets/escritorio.jpg';

const servicos = [
  { icon: FaBuilding, title: 'Sites Institucionais' },
  { icon: FaRocket, title: 'Landing Pages' },
  { icon: FaBookOpen, title: 'Catálogos Online' },
  { icon: FaUserMd, title: 'Sites para Clínicas e Profissionais Liberais' },
  { icon: FaBriefcase, title: 'Sites para Empresas e Prestadores de Serviços' },
  { icon: FaCogs, title: 'Sistemas Web Personalizados' },
  { icon: FaSearch, title: 'Otimização para Google (SEO)' },
  { icon: FaTools, title: 'Manutenção e Suporte' },
];

const diferenciais = [
  'Desenvolvimento personalizado (sem templates engessados quando o projeto exige)',
  'Sites rápidos, responsivos e preparados para conversão',
  'Estrutura otimizada para Google (SEO)',
  'Integração com WhatsApp, formulários e redes sociais',
  'Suporte durante todo o projeto',
  'Comunicação clara e atendimento próximo ao cliente',
];

const nichos = [
  'Advogados',
  'Psicólogos',
  'Nutricionistas',
  'Dentistas',
  'Médicos',
  'Academias',
  'Empresas',
  'Lojas',
  'Prestadores de serviços',
  'Pequenos e médios negócios',
];

const processo = [
  {
    numero: '01',
    icon: FaClipboardList,
    titulo: 'Briefing alinhado',
    descricao:
      'Entendo objetivos, prazo e referências — e alinhamos juntos o melhor caminho do projeto.',
  },
  {
    numero: '02',
    icon: FaFileAlt,
    titulo: 'Proposta e execução',
    descricao:
      'Defino escopo, prazos e entregáveis. Desenvolvo com atualizações claras em cada etapa importante.',
  },
  {
    numero: '03',
    icon: FaRocket,
    titulo: 'Entrega e suporte',
    descricao:
      'Publicação, ajustes finais e orientação para dar continuidade com autonomia.',
  },
];

const depoimentos = [
  {
    nome: 'Camila',
    empresa: 'Academia',
    texto:
      'A Monise desenvolveu o sistema de gestão e o site para minha academia. O trabalho foi impecável! Profissionalismo, atenção aos detalhes e sempre disponível para ajustes.',
  },
  {
    nome: 'Rosenal',
    empresa: 'Serviços de Jardinagem',
    texto:
      'Contratei a Monise para desenvolver o aplicativo de gestão. Ficou excelente! Organização completa de agendamentos e clientes, com entrega dentro do prazo.',
  },
  {
    nome: 'Renata',
    empresa: 'Clínica Odontológica',
    texto:
      'O site ficou moderno, elegante e transmite confiança. A comunicação foi excelente em todas as etapas e o prazo foi cumprido perfeitamente.',
  },
];

const Portfolio = () => {
  useSmoothScroll();
  useScrollAnimations();

  useEffect(() => {
    document.title = 'Apresentação | MS Digitais — Portfólio';
    document.documentElement.classList.add('preload');
    const timer = setTimeout(() => {
      document.documentElement.classList.remove('preload');
    }, 100);
    return () => {
      clearTimeout(timer);
      document.title = 'MS Digitais — Sites Institucionais | Monise Silva';
    };
  }, []);

  const sitesCount = projects.filter((p) => p.categoria === 'sites').length;
  const sistemasCount = projects.filter((p) => p.categoria === 'sistemas').length;

  const whatsappMsg = encodeURIComponent(
    'Olá, Monise! Vi sua apresentação / portfólio e gostaria de conversar.'
  );
  const whatsappHref = `https://wa.me/${siteConfig.whatsappNumber}?text=${whatsappMsg}`;

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 z-[1] bg-dark/65" />
        <div className="hero-gradient absolute inset-0 z-[1] opacity-35 mix-blend-multiply" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[520px] h-[520px] bg-burnt/25 rounded-full blur-[120px] z-[2] animate-glow" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-36">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-burnt font-heading font-semibold uppercase tracking-[0.28em] text-sm mb-5"
          >
            Apresentação / Portfólio
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading font-bold text-white leading-[0.95] mb-6"
          >
            <span className="block text-5xl md:text-7xl lg:text-8xl">MS Digitais</span>
            <span className="block mt-3 text-2xl md:text-3xl lg:text-4xl font-semibold text-cream/90 max-w-3xl">
              Sites e sistemas que unem design, estratégia e resultados
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="text-lg md:text-xl text-cream/80 font-body max-w-2xl mb-10 leading-relaxed"
          >
            Sou Monise Silva, desenvolvedora de sites e sistemas. Aqui você encontra meu perfil,
            serviços e projetos reais.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="flex flex-wrap gap-4"
          >
            <a
              href="#projetos"
              className="btn-premium inline-flex items-center justify-center px-7 py-3.5 rounded-xl bg-burnt text-white font-heading font-semibold hover:bg-primary-600 transition-colors shadow-premium"
              data-cursor="pointer"
            >
              Ver trabalhos
            </a>
            <a
              href="#o-que-desenvolvo"
              className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl border border-white/30 text-white font-heading font-semibold hover:bg-white/10 transition-colors"
              data-cursor="pointer"
            >
              O que desenvolvo
            </a>
          </motion.div>
        </div>
      </section>

      {/* Números */}
      <section className="py-16 bg-white dark:bg-dark-surface border-y border-brown/10 dark:border-burnt/10 transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: `${projects.length}+`, label: 'Projetos no portfólio' },
              { value: `${sitesCount}`, label: 'Sites' },
              { value: `${sistemasCount}`, label: 'Sistemas / apps' },
              { value: '24h', label: 'Resposta média' },
            ].map((item) => (
              <div key={item.label} className="gsap-reveal">
                <p className="text-3xl md:text-4xl font-heading font-bold text-burnt mb-1">{item.value}</p>
                <p className="text-sm text-dark/60 dark:text-cream/60 font-body">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quem sou */}
      <section id="apresentacao" className="py-24 md:py-32 bg-cream dark:bg-dark-bg transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <div className="gsap-reveal-left order-2 lg:order-1">
              <div className="relative max-w-md mx-auto lg:mx-0">
                <div className="absolute -inset-4 bg-burnt/10 rounded-2xl blur-2xl" />
                <ImageWithFallback
                  src={monisePhoto}
                  alt="Monise Silva — MS Digitais"
                  className="relative rounded-2xl shadow-premium dark:shadow-premium-dark w-full h-auto min-h-[360px] object-cover border border-brown/10 dark:border-burnt/20"
                  placeholderLabel="Monise Silva"
                />
              </div>
            </div>

            <div className="gsap-reveal-right order-1 lg:order-2">
              <p className="text-burnt font-heading font-semibold uppercase tracking-wider text-sm mb-4">
                Quem desenvolve
              </p>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark dark:text-cream mb-6">
                Monise Silva
              </h2>
              <p className="text-lg text-dark/80 dark:text-cream/80 leading-relaxed font-body">
                Sou Monise Silva, desenvolvedora de sites e sistemas, formada em Tecnologia da
                Informação e especializada na criação de soluções digitais que unem design,
                estratégia e resultados.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* O que desenvolvo */}
      <section id="o-que-desenvolvo" className="py-24 md:py-32 bg-white dark:bg-dark-surface transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 gsap-reveal">
            <p className="text-burnt font-heading font-semibold uppercase tracking-wider text-sm mb-4">
              Serviços
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark dark:text-cream mb-4">
              O que desenvolvo
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {servicos.map((servico) => {
              const Icon = servico.icon;
              return (
                <div
                  key={servico.title}
                  className="gsap-reveal flex items-start gap-3 p-5 rounded-2xl bg-cream dark:bg-dark-bg border border-brown/10 dark:border-burnt/15"
                >
                  <Icon className="text-burnt text-xl flex-shrink-0 mt-0.5" />
                  <h3 className="text-base font-heading font-semibold text-dark dark:text-cream leading-snug">
                    {servico.title}
                  </h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section id="diferenciais" className="py-24 md:py-32 bg-cream dark:bg-dark-bg transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 gsap-reveal">
            <p className="text-burnt font-heading font-semibold uppercase tracking-wider text-sm mb-4">
              Por que trabalhar comigo
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark dark:text-cream mb-4">
              Diferenciais
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {diferenciais.map((item) => (
              <div
                key={item}
                className="gsap-reveal flex items-start gap-4 p-5 rounded-2xl bg-white dark:bg-dark-surface border border-brown/10 dark:border-burnt/15"
              >
                <FaCheckCircle className="text-burnt text-xl flex-shrink-0 mt-0.5" />
                <p className="text-dark/80 dark:text-cream/80 font-body leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nichos */}
      <section id="nichos" className="py-24 md:py-32 bg-white dark:bg-dark-surface transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 gsap-reveal">
            <p className="text-burnt font-heading font-semibold uppercase tracking-wider text-sm mb-4">
              Experiência
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark dark:text-cream mb-4">
              Nichos atendidos
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {nichos.map((nicho) => (
              <span
                key={nicho}
                className="gsap-reveal px-4 py-2.5 text-sm md:text-base rounded-full bg-cream dark:bg-dark-bg border border-brown/10 dark:border-burnt/20 text-dark/80 dark:text-cream/80 font-body"
              >
                {nicho}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Processo */}
      <section id="processo" className="py-24 md:py-32 bg-dark-bg dark:bg-black text-cream relative overflow-hidden transition-colors duration-500">
        <div className="absolute inset-0 opacity-25 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-burnt/20 rounded-full blur-[140px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 gsap-reveal">
            <p className="text-burnt font-heading font-semibold uppercase tracking-wider text-sm mb-4">
              Fluxo de trabalho
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Como trabalhamos juntos
            </h2>
            <p className="text-xl text-cream/70 max-w-2xl mx-auto font-body">
              Um processo simples, transparente e fácil de acompanhar do início à entrega.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {processo.map((passo, index) => {
              const Icon = passo.icon;
              return (
                <div key={passo.numero} className="gsap-reveal relative text-center">
                  <div className="mb-6 flex justify-center">
                    <div className="relative bg-burnt text-white p-6 rounded-2xl">
                      <Icon className="text-3xl" />
                    </div>
                  </div>
                  <span className="text-5xl font-heading font-bold text-burnt/40">{passo.numero}</span>
                  <h3 className="text-2xl font-heading font-semibold text-white mt-2 mb-3">
                    {passo.titulo}
                  </h3>
                  <p className="text-cream/70 leading-relaxed font-body">{passo.descricao}</p>
                  {index < processo.length - 1 && (
                    <div className="hidden md:block absolute top-16 left-[calc(50%+60px)] w-[calc(100%-120px)] h-px bg-gradient-to-r from-burnt/50 to-transparent" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projetos */}
      <Projetos />

      {/* Depoimentos */}
      <section id="depoimentos-portfolio" className="py-24 md:py-32 bg-cream dark:bg-dark-bg transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 gsap-reveal">
            <p className="text-burnt font-heading font-semibold uppercase tracking-wider text-sm mb-4">
              Referências
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark dark:text-cream mb-4">
              O que clientes dizem
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {depoimentos.map((depoimento) => (
              <div
                key={depoimento.nome}
                className="gsap-reveal p-8 rounded-2xl bg-white dark:bg-dark-surface border border-brown/10 dark:border-burnt/10"
              >
                <div className="flex mb-4 gap-1">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-burnt text-sm" />
                  ))}
                </div>
                <p className="text-dark/90 dark:text-cream/90 mb-6 leading-relaxed italic font-body">
                  &ldquo;{depoimento.texto}&rdquo;
                </p>
                <div className="border-t border-brown/10 dark:border-burnt/10 pt-4">
                  <p className="font-heading font-semibold text-dark dark:text-cream">{depoimento.nome}</p>
                  <p className="text-sm text-dark/60 dark:text-cream/60">{depoimento.empresa}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contato */}
      <section id="contato-portfolio" className="py-24 md:py-32 bg-white dark:bg-dark-surface transition-colors duration-500">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="gsap-reveal">
            <p className="text-burnt font-heading font-semibold uppercase tracking-wider text-sm mb-4">
              Próximo passo
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark dark:text-cream mb-5">
              Vamos conversar?
            </h2>
            <p className="text-lg text-dark/70 dark:text-cream/70 font-body mb-10 leading-relaxed">
              Estou à disposição no WhatsApp para alinhar interesse e próximos passos.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-premium inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-xl bg-burnt text-white font-heading font-semibold hover:bg-primary-600 transition-colors shadow-premium"
                data-cursor="pointer"
              >
                <FaWhatsapp className="text-xl" />
                WhatsApp {siteConfig.whatsappDisplay}
              </a>
              <a
                href={siteConfig.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-xl border border-brown/20 dark:border-burnt/30 text-dark dark:text-cream font-heading font-semibold hover:border-burnt hover:text-burnt transition-colors"
                data-cursor="pointer"
              >
                <FaInstagram className="text-xl" />
                {siteConfig.instagramHandle}
              </a>
            </div>

            <p className="inline-flex items-center justify-center gap-2 text-dark/60 dark:text-cream/60 font-body text-sm mb-10">
              <FaEnvelope className="text-burnt" />
              Prefere e-mail? É só solicitar pelo WhatsApp
            </p>

            <p className="text-sm text-dark/50 dark:text-cream/50 font-body">
              <Link to="/" className="text-burnt hover:underline" data-cursor="pointer">
                Voltar ao site
              </Link>
            </p>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Portfolio;
